import { useTranscriptGeneration } from '../../shared/providers/TranscriptGenerationProvider';

import { PhoneCall } from './types';

export const useIsTranscriptGenerating = (phoneCall: PhoneCall) => {
    const { generatingTranscripts } = useTranscriptGeneration();

    return generatingTranscripts.has(phoneCall.id);
};

export const useIsSummaryGenerating = (phoneCall: PhoneCall) => {
    const { generatingSummaries } = useTranscriptGeneration();

    return generatingSummaries.has(phoneCall.id);
};

type TranscriptState = 'none' | 'loading' | 'ready';

type PhoneCallDisplayState = {
    showAudio: boolean;
    isAudioDeleted: boolean;

    hasSummary: boolean;
    hasTranscript: boolean;

    hasSummaryError: boolean;
    hasTranscriptError: boolean;
    isTranscriptDeleted: boolean;

    transcriptState: TranscriptState;
};

const getTranscriptState = (phoneCall: PhoneCall, isGenerating: boolean): TranscriptState => {
    const hasTranscriptError = Boolean(phoneCall.transcript_error_code);
    const hasSummaryError = Boolean(phoneCall.summary_error_code);

    if (hasTranscriptError || hasSummaryError) {
        return 'ready';
    }

    if (isGenerating || (phoneCall.raw_transcription === '' && !phoneCall.summary)) {
        return 'loading';
    }

    if (phoneCall.raw_transcription === null) {
        return 'none';
    }

    return 'ready';
};

export const usePhoneCallDisplayState = (phoneCall: PhoneCall): PhoneCallDisplayState => {
    const isGenerating = useIsTranscriptGenerating(phoneCall);

    const showAudio = Boolean(phoneCall.audio_file || phoneCall.recording_audio_file_deleted_at);
    const isAudioDeleted = Boolean(phoneCall.recording_audio_file_deleted_at);

    const hasSummary = Boolean(phoneCall.summary);
    const hasTranscript = Boolean(phoneCall.raw_transcription);

    const hasSummaryError = Boolean(phoneCall.summary_error_code);
    const hasTranscriptError = Boolean(phoneCall.transcript_error_code);
    const isTranscriptDeleted = Boolean(phoneCall.transcript_deleted_at);

    const transcriptState = getTranscriptState(phoneCall, isGenerating);

    return {
        showAudio,
        isAudioDeleted,
        hasSummary,
        hasTranscript,
        hasSummaryError,
        hasTranscriptError,
        isTranscriptDeleted,
        transcriptState,
    };
};
